import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  FaBuilding,
  FaUser,
  FaArrowLeft,
  FaIdCard,
  FaProjectDiagram,
  FaEnvelope,
  FaPhone,
  FaMobileAlt,
  FaGlobe,
  FaMapMarkerAlt,
  FaFileInvoiceDollar,
  FaPlus,
  FaChartPie,
  FaChevronDown,
  FaFileAlt,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import BusinessPartnerService, { BusinessPartner, LifecycleStage } from '../services/BusinessPartnerService';
import ProjectService, { Project } from '../services/ProjectService';
import ContactProfileTab from './ContactProfileTab';
import ProjectsTab from './ProjectsTab';
import ProjectModal from './ProjectModal';
import ContactOffersTab from './ContactOffersTab';
import ContactTasksSection from './ContactTasksSection';
import ContactOverviewPanel from './ContactOverviewPanel';
import HorizontalActivityTimeline from './HorizontalActivityTimeline';
import CreateOfferFromTemplateModal from '../../sales/components/CreateOfferFromTemplateModal';

type TabKey = 'overview' | 'profile' | 'projects' | 'offers';

interface TabConfig {
  key: TabKey;
  label: string;
  icon: React.ReactNode;
}

export default function ContactDetailPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [contact, setContact] = useState<BusinessPartner | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const tasksSectionRef = useRef<HTMLDivElement>(null);

  // Dropdown states for + Project and + Offer buttons
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [offerDropdownOpen, setOfferDropdownOpen] = useState(false);
  const projectDropdownRef = useRef<HTMLDivElement>(null);
  const offerDropdownRef = useRef<HTMLDivElement>(null);

  // Offer from Template Modal state
  const [offerTemplateModalOpen, setOfferTemplateModalOpen] = useState(false);

  const scrollToTasks = () => {
    tasksSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    if (id) {
      loadContact(parseInt(id, 10));
    }
  }, [id]);

  useEffect(() => {
    if (contact) {
      setActiveTab(contact.kind === 'CUSTOMER' ? 'overview' : 'profile');
    }
  }, [contact?.kind]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (projectDropdownRef.current && !projectDropdownRef.current.contains(event.target as Node)) {
        setProjectDropdownOpen(false);
      }
      if (offerDropdownRef.current && !offerDropdownRef.current.contains(event.target as Node)) {
        setOfferDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadContact = async (contactId: number) => {
    setLoading(true);
    try {
      const data = await BusinessPartnerService.get(contactId);
      setContact(data);
      if (data.kind === 'CUSTOMER') {
        const projectsData = await ProjectService.listByPartner(contactId);
        setProjects(projectsData);
      }
    } catch (err) {
      console.error('Failed to load contact:', err);
      toast.error('Failed to load contact');
      navigate('/contacts');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString?: string | null): string => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // Project dropdown handlers
  const handleNewProject = () => {
    setProjectDropdownOpen(false);
    setProjectModalOpen(true);
  };

  const handleProjectSaved = async () => {
    setProjectModalOpen(false);
    if (contact) {
      const projectsData = await ProjectService.listByPartner(contact.id);
      setProjects(projectsData);
      setActiveTab('projects');
    }
  };

  // Offer dropdown handlers
  const handleNewOffer = () => {
    setOfferDropdownOpen(false);
    // Offer can be created without a project - project is optional
    if (projects.length === 0) {
      // No projects - create offer without project
      navigate(`/offers/new?partner_id=${contact?.id}`);
    } else if (projects.length === 1) {
      // One project - auto-select it
      navigate(`/offers/new?partner_id=${contact?.id}&project_id=${projects[0].id}`);
    } else {
      // Multiple projects - let user choose (or they can create without project from offers/new)
      setActiveTab('projects');
      toast.info('Select a project or create offer from Projects tab');
    }
  };

  const handleOfferFromTemplate = () => {
    setOfferDropdownOpen(false);
    setOfferTemplateModalOpen(true);
  };

  const getKindColor = (kind: string): { bg: string; text: string } => {
    switch (kind) {
      case 'CUSTOMER':
        return { bg: '#dbeafe', text: '#1e40af' };
      case 'PARTNER':
        return { bg: '#d1fae5', text: '#065f46' };
      case 'SUPPLIER':
        return { bg: '#fef3c7', text: '#92400e' };
      default:
        return { bg: '#f3f4f6', text: '#374151' };
    }
  };

  const getStageColor = (stage: LifecycleStage): { bg: string; text: string } => {
    switch (stage) {
      case 'LEAD':
        return { bg: '#dbeafe', text: '#1e40af' };
      case 'PROSPECT':
        return { bg: '#e0e7ff', text: '#4338ca' };
      case 'ACTIVE':
        return { bg: '#dcfce7', text: '#166534' };
      case 'INACTIVE':
        return { bg: '#f1f5f9', text: '#64748b' };
      default:
        return { bg: '#f3f4f6', text: '#374151' };
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '60px', textAlign: 'center', color: '#666' }}>
        Loading...
      </div>
    );
  }

  if (!contact) {
    return (
      <div style={{ padding: '60px', textAlign: 'center', color: '#666' }}>
        Contact not found
      </div>
    );
  }

  const displayName = BusinessPartnerService.getDisplayName(contact);
  const kindColor = getKindColor(contact.kind);
  const addressParts = [contact.street, contact.house_no].filter(Boolean).join(' ');
  const cityParts = [contact.postal_code, contact.city].filter(Boolean).join(' ');
  const fullAddress = [addressParts, cityParts].filter(Boolean).join(', ');

  const tabs: TabConfig[] = [
    ...(contact.kind === 'CUSTOMER'
      ? [{ key: 'overview' as TabKey, label: 'Overview', icon: <FaChartPie /> }]
      : []),
    { key: 'profile', label: 'Profile', icon: <FaIdCard /> },
    ...(contact.kind === 'CUSTOMER'
      ? [
          { key: 'projects' as TabKey, label: 'Projects', icon: <FaProjectDiagram /> },
          { key: 'offers' as TabKey, label: 'Offers', icon: <FaFileInvoiceDollar /> },
        ]
      : []),
  ];

  return (
    <div style={{ padding: '32px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Back Button */}
      <Link
        to="/contacts"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 16px',
          marginBottom: '24px',
          background: '#f5f5f5',
          border: '1px solid #ddd',
          borderRadius: '6px',
          fontSize: '14px',
          fontWeight: 600,
          textDecoration: 'none',
          color: '#333',
        }}
      >
        <FaArrowLeft size={14} />
        Back to Contacts
      </Link>

      {/* Header Card */}
      <div
        style={{
          background: '#fff',
          border: '1px solid #e0e3ef',
          borderRadius: '12px',
          padding: '32px',
          marginBottom: '24px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', gap: '24px', flex: 1 }}>
            {/* Avatar */}
            <div
              style={{
                width: '100px',
                height: '100px',
                borderRadius: '50%',
                background: '#e0e3ef',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '36px',
                fontWeight: 700,
                color: '#021a4a',
              }}
            >
              {contact.is_company ? (
                <FaBuilding size={40} color="#021a4a" />
              ) : (
                <FaUser size={40} color="#021a4a" />
              )}
            </div>

            {/* Info */}
            <div style={{ flex: 1 }}>
              {/* Name */}
              <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#0f2b5a', margin: '0 0 12px 0' }}>
                {displayName}
              </h1>

              {/* Email & Phone Row */}
              <div style={{ display: 'flex', gap: '24px', marginBottom: '16px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FaEnvelope color="#666" size={14} />
                  <span style={{ fontSize: '14px', color: '#333' }}>
                    {contact.email ? (
                      <a href={`mailto:${contact.email}`} style={{ color: '#021a4a', textDecoration: 'none' }}>
                        {contact.email}
                      </a>
                    ) : '—'}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FaPhone color="#666" size={14} />
                  <span style={{ fontSize: '14px', color: '#333' }}>
                    {contact.phone ? (
                      <a href={`tel:${contact.phone}`} style={{ color: '#021a4a', textDecoration: 'none' }}>
                        {contact.phone}
                      </a>
                    ) : '—'}
                  </span>
                </div>
                {contact.mobile && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaMobileAlt color="#666" size={14} />
                    <span style={{ fontSize: '14px', color: '#333' }}>
                      <a href={`tel:${contact.mobile}`} style={{ color: '#021a4a', textDecoration: 'none' }}>
                        {contact.mobile}
                      </a>
                    </span>
                  </div>
                )}
              </div>

              {/* Address & Website Row */}
              <div style={{ display: 'flex', gap: '24px', marginBottom: '12px', flexWrap: 'wrap' }}>
                {fullAddress && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaMapMarkerAlt color="#666" size={14} />
                    <span style={{ fontSize: '14px', color: '#666' }}>
                      {fullAddress}
                    </span>
                  </div>
                )}
                {contact.website && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaGlobe color="#666" size={14} />
                    <a
                      href={contact.website.startsWith('http') ? contact.website : `https://${contact.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: '14px', color: '#021a4a', textDecoration: 'none' }}
                    >
                      {contact.website}
                    </a>
                  </div>
                )}
              </div>

              {/* Tax Info Row */}
              <div style={{ display: 'flex', gap: '24px', marginBottom: '16px', flexWrap: 'wrap' }}>
                {contact.vat_id && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaFileInvoiceDollar color="#666" size={14} />
                    <span style={{ fontSize: '14px', color: '#666' }}>
                      <strong>VAT ID:</strong> {contact.vat_id}
                    </span>
                  </div>
                )}
                {contact.tax_number && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaFileInvoiceDollar color="#666" size={14} />
                    <span style={{ fontSize: '14px', color: '#666' }}>
                      <strong>Tax No:</strong> {contact.tax_number}
                    </span>
                  </div>
                )}
              </div>

              {/* Contact Person for Company */}
              {contact.is_company && contact.first_name && contact.last_name && (
                <div style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
                  <strong>Contact person:</strong> {contact.first_name} {contact.last_name}
                </div>
              )}

              {/* Created Date */}
              <div style={{ fontSize: '13px', color: '#999' }}>
                Created: {formatDate(contact.created_at)}
              </div>
            </div>
          </div>

          {/* Status Badges & Action Buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '12px' }}>
            {/* Badges Row */}
            <div style={{ display: 'flex', gap: '8px' }}>
              <span
                style={{
                  padding: '6px 12px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 600,
                  background: kindColor.bg,
                  color: kindColor.text,
                }}
              >
                {BusinessPartnerService.getKindLabel(contact.kind)}
              </span>
              <span
                style={{
                  padding: '6px 12px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 600,
                  background: getStageColor(contact.lifecycle_stage).bg,
                  color: getStageColor(contact.lifecycle_stage).text,
                }}
              >
                {BusinessPartnerService.getStageLabel(contact.lifecycle_stage)}
              </span>
            </div>

            {/* Action Dropdown Buttons - only for CUSTOMER */}
            {contact.kind === 'CUSTOMER' && (
              <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                {/* Project Dropdown */}
                <div ref={projectDropdownRef} style={{ position: 'relative' }}>
                  <button
                    onClick={() => setProjectDropdownOpen(!projectDropdownOpen)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 16px',
                      border: '1px solid #e0e3ef',
                      borderRadius: '6px',
                      background: '#fff',
                      color: '#021a4a',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    <FaPlus size={12} />
                    Project
                    <FaChevronDown size={10} style={{ marginLeft: '4px' }} />
                  </button>

                  {projectDropdownOpen && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        marginTop: '4px',
                        background: 'white',
                        border: '1px solid #e0e3ef',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        minWidth: '180px',
                        zIndex: 100,
                        overflow: 'hidden',
                      }}
                    >
                      <button
                        onClick={handleNewProject}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          width: '100%',
                          padding: '12px 16px',
                          border: 'none',
                          background: 'none',
                          textAlign: 'left',
                          fontSize: '14px',
                          color: '#333',
                          cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f5f5')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                      >
                        <FaPlus size={12} color="#666" />
                        Neu
                      </button>
                    </div>
                  )}
                </div>

                {/* Offer Dropdown */}
                <div ref={offerDropdownRef} style={{ position: 'relative' }}>
                  <button
                    onClick={() => setOfferDropdownOpen(!offerDropdownOpen)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 16px',
                      border: 'none',
                      borderRadius: '6px',
                      background: '#021a4a',
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    <FaPlus size={12} />
                    Offer
                    <FaChevronDown size={10} style={{ marginLeft: '4px' }} />
                  </button>

                  {offerDropdownOpen && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        marginTop: '4px',
                        background: 'white',
                        border: '1px solid #e0e3ef',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        minWidth: '180px',
                        zIndex: 100,
                        overflow: 'hidden',
                      }}
                    >
                      <button
                        onClick={handleNewOffer}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          width: '100%',
                          padding: '12px 16px',
                          border: 'none',
                          background: 'none',
                          textAlign: 'left',
                          fontSize: '14px',
                          color: '#333',
                          cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f5f5')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                      >
                        <FaPlus size={12} color="#666" />
                        Neu
                      </button>
                      <button
                        onClick={handleOfferFromTemplate}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          width: '100%',
                          padding: '12px 16px',
                          border: 'none',
                          background: 'none',
                          textAlign: 'left',
                          fontSize: '14px',
                          color: '#333',
                          cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f5f5')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                      >
                        <FaFileAlt size={12} color="#666" />
                        Aus Vorlage
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Horizontal Activity Timeline - directly under header */}
      {contact.kind === 'CUSTOMER' && (
        <HorizontalActivityTimeline
          partnerCreatedAt={contact.created_at}
          projects={projects}
        />
      )}

      {/* Tabs */}
      <div
        style={{
          background: '#fff',
          border: '1px solid #e0e3ef',
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      >
        <div style={{ display: 'flex', borderBottom: '1px solid #e0e3ef' }}>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: '16px 24px',
                background: activeTab === tab.key ? '#fff' : '#f9f9f9',
                border: 'none',
                borderBottom: activeTab === tab.key ? '3px solid #021a4a' : '3px solid transparent',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
                color: activeTab === tab.key ? '#021a4a' : '#666',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{ padding: activeTab === 'overview' ? '0' : '24px', minHeight: '400px' }}>
          {activeTab === 'overview' && (
            <div style={{ padding: '24px' }}>
              {/* Overview Panel with Cards and Timeline */}
              <ContactOverviewPanel
                partnerId={contact.id}
                partnerCreatedAt={contact.created_at}
                projects={projects}
                onScrollToTasks={scrollToTasks}
                onCreateOffer={handleNewOffer}
                onCreateOfferFromTemplate={handleOfferFromTemplate}
              />

              {/* Tasks Section */}
              <div ref={tasksSectionRef}>
                <ContactTasksSection
                  partnerId={contact.id}
                  partnerName={displayName}
                />
              </div>
            </div>
          )}
          {activeTab === 'profile' && (
            <ContactProfileTab
              contact={contact}
              onContactUpdated={() => loadContact(contact.id)}
            />
          )}
          {activeTab === 'projects' && <ProjectsTab partnerId={contact.id} />}
          {activeTab === 'offers' && <ContactOffersTab partnerId={contact.id} />}
        </div>
      </div>

      {/* Project Modal */}
      <ProjectModal
        isOpen={projectModalOpen}
        project={null}
        partnerId={contact.id}
        onClose={() => setProjectModalOpen(false)}
        onSave={handleProjectSaved}
      />

      {/* Offer from Template Modal */}
      <CreateOfferFromTemplateModal
        isOpen={offerTemplateModalOpen}
        partnerId={contact.id}
        onClose={() => setOfferTemplateModalOpen(false)}
        onCreated={(offerId) => {
          setOfferTemplateModalOpen(false);
          setActiveTab('offers');
        }}
      />
    </div>
  );
}
